/**
 * Copias de seguridad del proyecto.
 *
 * Se guarda una en cada publicación —automática, sin que nadie la pida— y se
 * pueden crear a mano. Cuántas se conservan lo dice el plan (`BACKUP_LIMITS`):
 * al pasarse, la más vieja se va.
 *
 * Lo que se guarda es la FUENTE: páginas, CMS, tema y ajustes del sitio. No los
 * mensajes de formularios, ni los contactos, ni las ventas, ni los archivos
 * subidos — eso son cosas que pasaron, no contenido del sitio, y una copia del
 * sitio no puede deshacerlas. La pantalla lo dice porque es justo lo que la
 * gente asume mal de un botón que se llama «restaurar».
 */
import { useCallback, useEffect, useState } from 'react'
import {
  IconAlertCircle, IconArchive, IconCloudUpload, IconDots, IconEye, IconHistory,
  IconLoader2, IconRotate2, IconTrash,
} from '@tabler/icons-react'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { ConfirmDialog } from '../ConfirmDialog'
import { useToast } from '../../Toast'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { editorHref } from '../../../lib/editorSession'
import type { Translate } from '@webbuilder/i18n'
import { errorMessage } from './offerShared'
import {
  createProjectBackup, deleteProjectBackup, listProjectBackups,
  type ProjectBackup, type ProjectBackupList,
} from '../../../lib/api'
import { PlanUsageSummary } from '../PlanUsageSummary'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { ProfileAvatarContent } from '@webbuilder/ui'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'

/* Claves, no texto: constante de módulo. */
const KIND_LABEL: Record<ProjectBackup['kind'], string> = {
  publish: 'backups.kind.publish',
  manual: 'backups.kind.manual',
  pre_restore: 'backups.kind.preRestore',
}

function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString(activeLanguage(), {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** «8 páginas · 2 colecciones · 48 elementos», saltándose lo que está a cero. */
function contentLabel(backup: ProjectBackup, t: Translate): string {
  const parts = [t(backup.pageCount === 1 ? 'backups.contentPageOne' : 'backups.contentPageMany', { count: backup.pageCount })]
  if (backup.collectionCount > 0) {
    parts.push(t(backup.collectionCount === 1 ? 'backups.contentCollectionOne' : 'backups.contentCollectionMany', { count: backup.collectionCount }))
  }
  if (backup.cmsItemCount > 0) {
    parts.push(t(backup.cmsItemCount === 1 ? 'backups.contentItemOne' : 'backups.contentItemMany', { count: backup.cmsItemCount }))
  }
  return parts.join(' · ')
}

export function BackupsSection({ projectId, token, projectRole = 'owner', onBack, onRestore, onOpenPlans }: {
  projectId: string
  token: string
  onBack?: (() => void) | undefined
  /** Lleva al editor a confirmar y aplicar la restauración. */
  /**
   * Qué eres en el proyecto.
   *
   * Tres permisos distintos conviven aquí. La sección se ABRE con
   * `backups.read`; el botón de guardar una copia pide `backups.create`, que
   * sólo añade; y restaurar y borrar piden `backups.manage`, porque la primera
   * sobrescribe el proyecto entero y la segunda no se deshace. El Editor tiene
   * los dos primeros: ver el historial de su propio trabajo y guardar antes de
   * tocar algo grande es parte de editarlo.
   */
  projectRole?: ProjectAccessRole
  onRestore: (backupId: string) => void
  onOpenPlans: () => void
}) {
  /* Dos permisos y no uno: crear una copia sólo añade, restaurar y borrar
     deshacen. El Editor tiene el primero. */
  const canCreate = can(projectRole, 'backups.create')
  const canManage = can(projectRole, 'backups.manage')
  const { showToast } = useToast()
  const { t } = useI18n()
  const [data, setData] = useState<ProjectBackupList | null>(null)
  const [loading, setLoading] = useState(true)
  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const backupsLoader = useRecordLoader(loading)
  const [failed, setFailed] = useState('')
  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<ProjectBackup | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [listMenu, setListMenu] = useState<{ up: boolean; maxHeight: number }>({ up: false, maxHeight: 0 })

  const load = useCallback(async () => {
    try {
      setData(await listProjectBackups(projectId, token))
      setFailed('')
    } catch (error) {
      setFailed(errorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  const backups = data?.backups ?? []
  const limit = data?.limit ?? 0
  const full = backups.length >= limit && limit > 0

  const handleCreate = async () => {
    if (creating) return
    setCreating(true)
    try {
      await createProjectBackup(projectId, token)
      await load()
      showToast(t('backups.created'))
    } catch (error) {
      showToast(errorMessage(error), 'error')
    } finally {
      setCreating(false)
    }
  }

  /* La copia se abre donde vive el renderizador de publicación. Una pestaña
     aparte le da el viewport real y evita encoger el sitio dentro de un modal. */
  const openPreview = (backupId: string) => {
    const url = editorHref(`?pid=${encodeURIComponent(projectId)}&backupPreview=${encodeURIComponent(backupId)}`)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  /* La espera sustituye a la pantalla ENTERA, título y descripción incluidos:
     estando dentro de la tarjeta, la cabecera se pintaba sobre un hueco y luego
     se recolocaba al llegar el contenido. Ver `SectionLoader`. */
  if (backupsLoader.visible) return <SectionLoader loader={backupsLoader} label={t('backups.loading')} />

  return (
    <ProjectSettingsLayout
      title={t('backups.title')}
      description={t('backups.description')}
      onBack={onBack}
    >
      {/* El cupo, encima de la tarjeta y a la derecha. Dentro del encabezado
          quedaba pegado al botón y a la descripción: tres cosas compitiendo en
          una línea. Aquí se lee solo, y sigue estando donde la vista llega antes
          de recorrer la lista. */}
      {data ? (
        <div className="dash-backups-quota">
          <PlanUsageSummary
            items={[{ label: t('backups.quota'), used: backups.length, limit }]}
            plan={data.plan}
            onOpenPlans={onOpenPlans}
          />
        </div>
      ) : null}

      <section className="dash-settings-card dash-general-card dash-backups-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconHistory size={18} /></span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('backups.versions')}</h3>
            {/* El cupo ya lo dice la pastilla de al lado, así que aquí sólo va
                lo que ella no puede decir: qué pasa al llegar al tope. */}
            <p>{t('backups.versionsBody')}</p>
          </div>
          <div className="dash-billing-head-actions">
            {canCreate ? (
            <button
              type="button"
              className="dash-primary-btn"
              disabled={creating || full}
              title={full ? t('backups.full', { limit }) : undefined}
              onClick={() => void handleCreate()}
            >
              {creating ? <IconLoader2 size={14} className="dash-spin" /> : <IconCloudUpload size={14} />} {t('backups.create')}
            </button>
            ) : null}
          </div>
        </div>

        <div className="dash-billing-panel">
          {failed ? (
            <div className="dash-placeholder">
              <IconAlertCircle size={34} aria-hidden="true" />
              <strong>{t('backups.loadFailed')}</strong>
              <span>{failed}</span>
            </div>
          ) : backups.length === 0 ? (
            /* El mismo hueco que el resto del panel —icono de trazo fino,
               titular y frase—: sin botón, porque una copia no se crea desde
               aquí para empezar, la crea publicar. */
            <div className="dash-placeholder">
              <IconArchive size={34} aria-hidden="true" />
              <strong>{t('backups.empty')}</strong>
              <span>{t('backups.emptyBody')}</span>
            </div>
          ) : (
            <>
              {backups.map((backup) => (
                <div className="dash-billing-box dash-billing-list-row" key={backup.id}>
                  <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-billing-row-icon is-slate" aria-hidden="true">
                    <IconArchive size={15} />
                  </span>
                  <span className="dash-billing-list-copy">
                    <strong>
                      {backup.label || whenLabel(backup.createdAt)}
                      <em className="dash-billing-pill">{t(KIND_LABEL[backup.kind])}</em>
                    </strong>
                    <small className="dash-backup-meta">
                      <span>
                        {backup.label ? `${whenLabel(backup.createdAt)} · ` : ''}
                        {contentLabel(backup, t)} · {sizeLabel(backup.sizeBytes)}
                      </span>

                      {/* Quién la hizo, en la misma línea que el resto de los
                          datos de la copia y NO como una cuarta columna: la fila
                          es una rejilla de tres —icono, texto, acciones— y un
                          hijo de más empujaba los botones a un renglón propio.

                          Con el nombre a la vista y no sólo al pasar por encima:
                          en un equipo, «quién publicó esto» es la mitad de lo que
                          se viene a mirar aquí, y un dato que hay que descubrir
                          con el puntero no está para quien usa el teclado. */}
                      {backup.author ? (
                        <span className="dash-backup-author">
                          <span className="dash-backup-author-face" aria-hidden="true">
                            {backup.author.avatarUrl
                              ? <img src={backup.author.avatarUrl} alt="" />
                              : <ProfileAvatarContent name={backup.author.name || backup.author.email || '?'} />}
                          </span>
                          {backup.author.name || backup.author.email}
                        </span>
                      ) : null}
                    </small>
                  </span>
                  {/* Las mismas acciones que cualquier fila del panel: los
                      botones de icono con su globo, y lo destructivo dentro del
                      menú de los tres puntos. Borrar una copia no puede estar a
                      un clic de distancia al lado de «Ver». */}
                  <span className="dash-billing-list-side dash-project-row-actions">
                    <button
                      type="button"
                      className="dash-icon-btn"
                      aria-label={`${t('backups.view')}: ${whenLabel(backup.createdAt)}`}
                      data-tooltip={t('backups.view')}
                      onClick={() => openPreview(backup.id)}
                    >
                      <IconEye size={16} />
                    </button>
                    {canManage ? (
                      <button
                        type="button"
                        className="dash-icon-btn"
                        aria-label={`${t('backups.restore')}: ${whenLabel(backup.createdAt)}`}
                        data-tooltip={t('backups.restore')}
                        onClick={() => onRestore(backup.id)}
                      >
                        <IconRotate2 size={16} />
                      </button>
                    ) : null}

                    <div className={`dash-project-row-menu-wrap${menuOpenId === backup.id ? ' is-open' : ''}${menuOpenId === backup.id && listMenu.up ? ' menu-up' : ''}`}>
                      <button
                        className="project-menu-btn dash-icon-btn dash-page-options-btn"
                        type="button"
                        aria-label={t('backups.moreOptions')}
                        data-tooltip={t('backups.options')}
                        aria-expanded={menuOpenId === backup.id}
                        onClick={(event) => {
                          event.stopPropagation()
                          // Se mide al abrir: en las últimas filas no cabe hacia
                          // abajo y el menú se salía de la pantalla. Mismo
                          // cálculo que en Páginas y Formularios.
                          if (menuOpenId !== backup.id) {
                            const rect = event.currentTarget.getBoundingClientRect()
                            const spaceBelow = window.innerHeight - rect.bottom - 16
                            const spaceAbove = rect.top - 80
                            const up = spaceBelow < 200 && spaceAbove > spaceBelow
                            setListMenu({ up, maxHeight: Math.max(120, up ? spaceAbove : spaceBelow) })
                          }
                          setMenuOpenId(menuOpenId === backup.id ? null : backup.id)
                        }}
                      >
                        <IconDots size={16} />
                      </button>
                      {menuOpenId === backup.id ? (
                        <>
                          <div className="project-menu-backdrop" role="presentation" onClick={() => setMenuOpenId(null)} />
                          <div className="project-menu dash-project-row-menu" role="menu" style={{ maxHeight: listMenu.maxHeight, overflowY: 'auto' }}>
                            <button
                              className="project-menu-item"
                              type="button"
                              role="menuitem"
                              onClick={() => { setMenuOpenId(null); openPreview(backup.id) }}
                            >
                              <IconEye size={14} /> {t('backups.view')}
                            </button>
                            {canManage ? (
                              <>
                                <button
                                  className="project-menu-item"
                                  type="button"
                                  role="menuitem"
                                  onClick={() => { setMenuOpenId(null); onRestore(backup.id) }}
                                >
                                  <IconRotate2 size={14} /> {t('backups.restore')}
                                </button>
                                <div className="project-menu-rule" role="separator" />
                                <button
                                  className="project-menu-item danger"
                                  type="button"
                                  role="menuitem"
                                  onClick={() => { setMenuOpenId(null); setToDelete(backup) }}
                                >
                                  <IconTrash size={14} /> {t('backups.delete')}
                                </button>
                              </>
                            ) : null}
                          </div>
                        </>
                      ) : null}
                    </div>
                  </span>
                </div>
              ))}

              <p className="dash-billing-note">
                <IconAlertCircle size={13} />
                <span>{t('backups.note')}</span>
              </p>
            </>
          )}
        </div>
      </section>

      {toDelete ? (
        <ConfirmDialog
          title={t('backups.confirmDelete')}
          message={t('backups.deleteMessage', { date: whenLabel(toDelete.createdAt) })}
          confirmLabel={t('backups.deleteConfirm')}
          confirmingLabel={t('backups.deleting')}
          tone="danger"
          onConfirm={async () => {
            const backup = toDelete
            setToDelete(null)
            try {
              await deleteProjectBackup(projectId, backup.id, token)
              await load()
              showToast(t('backups.deleted'))
            } catch (error) {
              showToast(errorMessage(error), 'error')
            }
          }}
          onCancel={() => setToDelete(null)}
        />
      ) : null}
    </ProjectSettingsLayout>
  )
}
